// videoFrameStep.ts

/**
 * Frame-by-frame stepping for the Express WebM clips.
 *
 * A browser video does not report its frame rate, and each clip keeps the rate of its
 * DICOM. So it is measured while the clip plays, two ways:
 *  - the shortest gap between presented frames — exact, but a whole number of frames,
 *    because with many clips on screen the browser can skip frames the whole time;
 *  - media time played / frames decoded (getVideoPlaybackQuality counts dropped frames
 *    too) — right number of frames per gap, but a few percent off over a short stretch.
 * The second says how many frames the shortest gap spans; the first gives the length.
 */

/** Used until a clip has played a couple of frames. */
export const FALLBACK_FRAME_SECONDS = 1 / 30;

const frameSeconds = new WeakMap<HTMLVideoElement, number>();
const watched = new WeakSet<HTMLVideoElement>();

/** Starts measuring the clip's frame length. Safe to call on every render. */
export function watchFrameDuration(video: HTMLVideoElement | null): void {
  if (!video || watched.has(video) || typeof video.requestVideoFrameCallback !== 'function') {
    return;
  }
  watched.add(video);
  const decodedFrames = () =>
    typeof video.getVideoPlaybackQuality === 'function'
      ? video.getVideoPlaybackQuality().totalVideoFrames
      : null;
  // Start of the current uninterrupted stretch of playback, and the last presented frame.
  let anchor: { mediaTime: number; frames: number } | null = null;
  let lastMediaTime: number | null = null;
  let minGap: number | null = null;
  let decodedEstimate: number | null = null;
  // A seek or step breaks the stretch.
  video.addEventListener('seeking', () => {
    anchor = null;
    lastMediaTime = null;
  });
  const onFrame = (_now: number, meta: VideoFrameCallbackMetadata) => {
    if (video.paused || video.seeking) {
      anchor = null;
      lastMediaTime = null;
      video.requestVideoFrameCallback(onFrame);
      return;
    }
    if (lastMediaTime !== null) {
      const gap = meta.mediaTime - lastMediaTime;
      // Loop restarts give a negative gap; long gaps are stalls.
      if (gap > 0.001 && gap < 0.5 && (minGap === null || gap < minGap)) {
        minGap = gap;
      }
    }
    lastMediaTime = meta.mediaTime;

    const frames = decodedFrames();
    if (frames !== null) {
      if (!anchor || meta.mediaTime <= anchor.mediaTime || frames < anchor.frames) {
        // First frame, or the loop restarted.
        anchor = { mediaTime: meta.mediaTime, frames };
      } else if (frames - anchor.frames >= 5) {
        const estimate = (meta.mediaTime - anchor.mediaTime) / (frames - anchor.frames);
        if (estimate > 0.001 && estimate < 0.5) {
          decodedEstimate = estimate;
        }
      }
    }

    const frame =
      minGap !== null && decodedEstimate !== null
        ? minGap / Math.max(1, Math.round(minGap / decodedEstimate))
        : (minGap ?? decodedEstimate);
    if (frame !== null) {
      frameSeconds.set(video, frame);
    }
    video.requestVideoFrameCallback(onFrame);
  };
  video.requestVideoFrameCallback(onFrame);
}

/**
 * Time to seek to for the next (dir 1) or previous (dir -1) frame. Lands mid-frame so
 * rounding never shows the neighbouring frame, and wraps at either end like the loop.
 */
export function frameStepTime(
  currentTime: number,
  frameDuration: number,
  duration: number,
  dir: 1 | -1
): number {
  const frameCount = Math.max(1, Math.floor(duration / frameDuration));
  const current = Math.min(frameCount - 1, Math.floor(currentTime / frameDuration));
  const next = (current + dir + frameCount) % frameCount;
  return (next + 0.5) * frameDuration;
}

/** Steps a paused clip one frame. Playing or unloaded clips are left alone. */
export function stepVideoFrame(video: HTMLVideoElement | null, dir: 1 | -1): boolean {
  if (!video || !video.paused || !Number.isFinite(video.duration) || video.duration <= 0) {
    return false;
  }
  const frame = frameSeconds.get(video) ?? FALLBACK_FRAME_SECONDS;
  video.currentTime = frameStepTime(video.currentTime, frame, video.duration, dir);
  return true;
}
